import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  DialogContent,
  DialogContentText,
  Typography,
  Button,
  useMediaQuery,
  Box,
} from '@mui/material';
import { useAccount } from '../../../contexts/Account';
import DefaultAdvisorAvatar from '../../../icons/user-avatar.svg';

const useStyles = ({ isSM }) => ({
  ownerImageWrapper: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
    marginTop: '20px',
  },
  dialogContent: {
    marginTop: isSM ? '0px' : '-30px',
    padding: isSM ? '20px' : '16px 0px',
  },
  dialogOwnerImage: {
    width: '60px',
    height: '60px',
    maxHeight: '60px',
    maxWidth: '60px',
    borderRadius: '146px',
    marginRight: '20px',
    cursor: 'pointer',
  },
  dialogText: {
    textAlign: 'left',
    mx: '0px',
    px: { xxs: '60px', xs: '0px' },
  },
  dialogTextCompanyName: {
    display: 'flex',
    flexDirection: 'column',
    textAlign: 'center',
    maxWidth: '100%',
  },
  dialogTextCompanyNameContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  dialogBoardOwnerContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
});

export default function Step1({ companyBoardData, setActiveStep }) {
  const params = useParams('/:companySlug/boardrooms');
  const { companySlug } = params;
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const { accountDetails, asLoggedUserDetails } = useAccount();
  const [companyBoardOwner, setCompanyBoardOwner] = useState();
  const [companyIsP2P, setcompanyIsP2P] = useState();

  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const classes = useStyles({ isSM });

  useEffect(() => {
    let isMounted = true;

    const fetchCompanyData = () => {
      try {
        if (isMounted && companyBoardData) {
          const companyContacts = companyBoardData.contacts || [];
          const companyOwner = companyContacts.find((c) => c.isBoardOwner);
          setCompanyBoardOwner(companyOwner || companyContacts[0]);
          setcompanyIsP2P(companyBoardData.boardType == "P2P");
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
      }
    };

    const initializeModal = () => {
      if (!companySlug) return;

      const selectedCompany =
        asLoggedUserDetails.advisorRelatedCompanies.memberBoards.find(
          (element) => element.slug === companySlug
        );

      if (selectedCompany?.id) {
        fetchCompanyData(selectedCompany.id);
      }
    };

    initializeModal();

    return () => {
      isMounted = false;
    };
  }, [companySlug, asLoggedUserDetails, queryParams]);

  function getTextTrimmed(text, maxSize) {
    if (!text) return '';
    if (text.length <= maxSize) {
      return text;
    }
    return text.substring(0, maxSize) + '...';
  }

  return (
    <DialogContent sx={classes.dialogContent}>
      <div style={classes.dialogTextCompanyNameContainer}>
        <DialogContentText sx={classes.dialogTextCompanyName}>
          <Typography
            variant="caption"
            sx={{
              fontSize: '16px',
              lineHeight: '24px',
              letterSpacing: '-0.43px',
              textTransform: 'none',
              fontWeight: 700,
              color: '#232B35',
              verticalAlign: 'middle',
              marginTop: '8px',
            }}
          >
            { companyIsP2P ? 
              `Welcome, ${accountDetails.givenName} ${accountDetails.surName}` :
              `${accountDetails.givenName}, welcome to our advisory board!` }
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: '#232B35',
              lineHeight: '20px',
              letterSpacing: '-0.43px',
              mt: '24px',
            }}
          >
            Here's how it works:
          </Typography>
        </DialogContentText>
      </div>
      <DialogContent sx={classes.dialogText}>
        <ol
          style={{
            listStyleType: 'decimal',
            fontSize: 13,
            paddingLeft: 20,
            color: '#232B35',
            margin: '0',
          }}
        >
          <li style={{ marginBottom: 12 }}>
            You can interact in a variety of ways. Answer surveys, share ideas,
            or even make introductions.
          </li>
          <li style={{ marginBottom: 12 }}>
            { companyIsP2P ? 
              "Every week you’ll be able to participate in any video workshops in our Boardroom that align with your interests and expertise." :
              "Every month you'll receive a Board Member Update email that makes it easy for you to share your ideas and stay up to date."}
            
          </li>
          <li style={{ marginBottom: 12 }}>
            { companyIsP2P ? 
              "Feel free to reach out to fellow advisors, trade best practices, and enjoy high-level networking through this boardroom as well!" :
              "All we ask is that you provide your unique insights at least once per month as part of our board!" }
          </li>
        </ol>
      </DialogContent>
      {companyBoardOwner && (
        <div role="none" style={classes.ownerImageWrapper}>
          <img
            alt=""
            style={classes.dialogOwnerImage}
            src={
              companyBoardOwner?.image?.id &&
              !companyBoardOwner.image.id.includes('undefined')
                ? `${process.env.REACT_APP_IMAGES_URL}${companyBoardOwner.image.id}`
                : DefaultAdvisorAvatar
            }
          />
          <div style={classes.dialogBoardOwnerContainer}>
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'Poppins',
                fontSize: 13,
                fontWeight: 500,
                color: '#232B35',
              }}
            >
              {companyBoardOwner.displayName}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontFamily: 'Poppins',
                fontSize: 13,
                fontWeight: 400,
                color: '#232B35',
              }}
            >
              {`${companyBoardOwner.title} - ${companyBoardData.displayName}`}
            </Typography>
          </div>
        </div>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: '28px',
        }}
      >
        <Button
          id={`enterBoardroom${companyBoardData?.displayName.replace(
            /\s+/g,
            ''
          )}`}
          variant="contained"
          color="primary"
          onClick={() => {
            setActiveStep((prev) => Math.min(prev + 1, 2));
          }}
          className="getStartedButton"
          style={{
            backgroundColor: '#3578F4',
            paddingTop: 15,
            paddingBottom: 15,
            textTransform: 'none',
            fontFamily: 'Poppins',
            fontWeight: 700,
            fontSize: 15,
            width: 180,
            height: 44,
            borderRadius: 30,
          }}
        >
          Get Started
        </Button>
      </Box>
    </DialogContent>
  );
}
